/* eslint camelcase: ["error", {allow: ["is_locked", "default_max_distance", "default_max_weight", "default_max_volume", "default_time_window", "default_provides"]}] */
import { Grid } from '@mui/material';
import { HSpacer, RoutalDialog, RoutalLoader, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { getVehicleColor } from '../../../constants/Colors';
import * as routing from '../../../constants/Routing';
import { Project } from '../../../domain/project/Project';
import { Vehicle } from '../../../domain/vehicle/Vehicle';
import { useCreatePlanWithDrivers } from '../../../hooks/plan/useCreatePlanWithDrivers';
import { useSetActiveLanguage } from '../../../hooks/translations/useSetActiveLanguage';
import Button from '../../../react-components/Button';
import { Map } from '../../../react-components/Map';
import Text from '../../../react-components/Text';

type VehiclesCreatePlanDialogProps = {
  open: boolean;
  selectedVehicles: Vehicle[];
  activeProject?: Project;
  onCloseDialog: (...args: any[]) => any;
};

const VehiclesCreatePlanDialog = ({
  open,
  selectedVehicles,
  activeProject,
  onCloseDialog,
}: VehiclesCreatePlanDialogProps) => {
  const navigate = useNavigate();
  const { data: activeLanguage } = useSetActiveLanguage();
  const {
    run: createPlan,
    data: createdPlanId,
    success: planCreated,
    isLoading: isCreating,
  } = useCreatePlanWithDrivers();

  useEffect(() => {
    if (planCreated && createdPlanId) {
      onCloseDialog();
      navigate(
        routing.PLANNER.PLAN_DETAILS.OVERVIEW.replace(`:projectId`, activeProject!.id).replace(`:planId`, createdPlanId)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planCreated, createdPlanId]);

  return (
    <RoutalDialog
      open={open}
      onCloseDialog={onCloseDialog}
      titleContent={<Text variant="h6" intlId="vehicles.create_plan_dialog.title" />}
      footerContent={
        <Grid container justifyContent="flex-end">
          <Button onClick={() => onCloseDialog()} variant="flat" intlId="cancel" />
          <HSpacer small />
          <Button onClick={() => createPlan({ vehicles: selectedVehicles })} intlId="confirm" />
        </Grid>
      }
    >
      {isCreating ? (
        <Grid
          item
          container
          direction="column"
          style={{ height: `100%`, width: `100%` }}
          alignItems="center"
          justifyContent="center"
        >
          <RoutalLoader />
        </Grid>
      ) : (
        <>
          <Text intlId="vehicles.create_plan_dialog.content" intlValues={{ data: selectedVehicles.length }} />

          <VSpacer medium />

          <Grid item container direction="row" justifyContent="center" alignItems="center">
            <Map
              key="map"
              height={300}
              language={activeLanguage?.locale ?? `en`}
              markers={selectedVehicles
                .filter(
                  (vehicle) => vehicle.default_start_location?.isValid() || vehicle.default_end_location?.isValid()
                )
                .map((vehicle) => ({
                  id: vehicle.id,
                  label: vehicle.label,
                  lat: (vehicle.default_start_location?.lat ?? vehicle.default_end_location?.lat)!,
                  lng: (vehicle.default_start_location?.lng ?? vehicle.default_end_location?.lng)!,
                  color: getVehicleColor(vehicle, selectedVehicles),
                  kind: `default`,
                }))}
            />
          </Grid>
        </>
      )}
    </RoutalDialog>
  );
};

export default VehiclesCreatePlanDialog;
